import ApplicationController from "./application_controller";
import AirDatepicker from "air-datepicker";
import fr from "air-datepicker/locale/fr";
import en from "air-datepicker/locale/en";
import Inputmask from "inputmask";

export default class extends ApplicationController {
  static values = {
    minDate: String,
    maxDate: String,
    reflex: String,
    format: String,
    timepicker: Boolean,
    locale: String,
    name: String,
    id: String,
    disabled: Boolean,
  };

  connect() {
    // --- Locale ---
    let selectedLocale = fr;
    if (this.hasLocaleValue && this.localeValue === "en") selectedLocale = en;

    // --- Format date ---
    const finalFormat = this.hasFormatValue
      ? this.formatValue
      : this.localeValue === "en"
      ? "MM/dd/yyyy"
      : "dd/MM/yyyy";

    // --- disabled field ---
    this.isDisabled =
      this.element.hasAttribute("disabled") ||
      (this.hasDisabledValue && this.disabledValue);

    if (this.isDisabled) {
      this.element.setAttribute("disabled", "disabled");
    } else {
      this.element.removeAttribute("disabled");
    }

    this.element.style.boxSizing = "content-box";

    // Parsing bornes (timezone-safe)
    const minDateParsed = this.hasMinDateValue
      ? this.parseAnyDateStringToLocalDate(this.minDateValue, finalFormat)
      : undefined;

    const maxDateParsed = this.hasMaxDateValue
      ? this.parseAnyDateStringToLocalDate(this.maxDateValue, finalFormat)
      : undefined;

    // Valeur par défaut (timezone-safe)
    const defaultDate = this.element.value
      ? this.parseAnyDateStringToLocalDate(this.element.value, finalFormat)
      : null;

    // --- Forcer affichage correct pour <input type="time"> ---
    if (this.element.type === "time") {
      this.element.type = "text";
    }

    // --- Initialisation AirDatepicker ---
    this.picker = new AirDatepicker(this.element, {
      onlyTimepicker:
        this.element.classList.contains("time-only") ||
        (this.hasTimepickerValue &&
          this.timepickerValue &&
          this.element.dataset.onlyTime === "true"),
      autoClose: true,
      timepicker: this.hasTimepickerValue ? this.timepickerValue : false,
      timeFormat: "HH:mm",
      dateFormat: finalFormat,
      locale: selectedLocale,
      isMobile: false,
      minDate: minDateParsed,
      maxDate: maxDateParsed,
      selectedDates: defaultDate ? [defaultDate] : [],
      view: "days",
      buttons: [
        {
          content: this.localeValue === "en" ? "Today" : "Aujourd’hui",
          className: "custom-today-btn",
          onClick: (dp) => {
            const today = new Date();
            dp.selectDate(today, true);
            dp.setViewDate(today);
          },
        },
        "clear",
      ],
      navTitles: { days: "MMMM yyyy" },
      prevHtml: "&larr;",
      nextHtml: "&rarr;",
      onSelect: ({ date }) => this.handleSelect(date),
    });
    // CORRECTIF REFLEX : Force AirDatepicker à écrire la date au bon format dans l'input
    if (defaultDate && !isNaN(defaultDate.getTime())) {
      this.picker.selectDate(defaultDate, { silent: true });
    }

    // --- Créer ou retrouver le champ hidden associé ---
    const existing = document.getElementById(this.idValue);
    if (existing && existing.tagName === "INPUT" && existing.type === "hidden") {
      this.hiddenInput = existing;
    } else {
      this.hiddenInput = document.createElement("input");
      this.hiddenInput.type = "hidden";
      this.hiddenInput.id = this.idValue;
      this.hiddenInput.name = this.nameValue;
      this.element.parentNode.insertBefore(this.hiddenInput, this.element.nextSibling);
    }

    // --- Empêcher le champ visible d'être soumis ---
    this.element.removeAttribute("name");
    this.hiddenInput.name = this.nameValue;

    // --- Valeur initiale du hidden (timezone-safe) ---
    const parsed = this.parseAnyDateStringToLocalDate(this.element.value, finalFormat);
    this.hiddenInput.value =
      parsed && !isNaN(parsed.getTime()) ? this.toRailsDateFormat(parsed) : "";

    // --- Inputmask (on garde TES masques pour restaurer la saisie clavier des heures) ---
    if (this.picker.onlyTimepicker) {
      // Mode "heure seule"
      Inputmask({
        mask: "h9:s9",
        placeholder: "__:__",
        alias: "datetime",
        hourFormat: "24",
        showMaskOnHover: false,
        // options qui évitent de bloquer la frappe selon config navigateur
        clearIncomplete: false,
        jitMasking: true,
      }).mask(this.element);
    } else if (this.hasTimepickerValue && this.timepickerValue) {
      // Mode "date + heure"
      Inputmask({
        mask: "99/99/9999 99:99",
        placeholder: "__/__/____ __:__",
        showMaskOnHover: false,
        clearIncomplete: false,
        jitMasking: true,
      }).mask(this.element);
    } else {
      // Mode "date seule"
      Inputmask({
        mask: "99/99/9999",
        placeholder: "__/__/____",
        showMaskOnHover: false,
        clearIncomplete: false,
        jitMasking: true,
      }).mask(this.element);
    }

    // --- Form submit sync ---
    this.form = this.element.closest("form");
    if (this.form) {
      this.submitListener = this.handleFormSubmit.bind(this);
      this.form.addEventListener("submit", this.submitListener);
    }

    // --- Synchroniser quand l'utilisateur saisit manuellement ---
    this.element.addEventListener("blur", () => {
  setTimeout(() => {
    const inputValue = this.element.value.trim();
    const inputDate = this.parseInputDate(inputValue, finalFormat);

    // ⚠️ Date invalide
    if (!inputDate || isNaN(inputDate.getTime())) {
      this.element.value = "";
      this.hiddenInput.value = "";
      this.element.classList.add("fx-error");
      setTimeout(() => this.element.classList.remove("fx-error"), 3000);
      return;
    }

    // ✅ Gestion bornes
    if (minDateParsed && inputDate < minDateParsed) {
      if (!isNaN(minDateParsed.getTime())) {
        this.picker.selectDate(minDateParsed, true);
        this.picker.setViewDate(minDateParsed);
        this.hiddenInput.value = this.toRailsDateFormat(minDateParsed);
      }
      return;
    }

    if (maxDateParsed && inputDate > maxDateParsed) {
      if (!isNaN(maxDateParsed.getTime())) {
        this.picker.selectDate(maxDateParsed, true);
        this.picker.setViewDate(maxDateParsed);
        this.hiddenInput.value = this.toRailsDateFormat(maxDateParsed);
      }
      return;
    }

    // ✅ Ajout : gestion de l’heure si timepicker activé
    if (this.hasTimepickerValue && this.timepickerValue) {
      const timeMatch = inputValue.match(/(\d{1,2}):(\d{1,2})/);
      if (timeMatch) {
        const hours = parseInt(timeMatch[1], 10);
        const minutes = parseInt(timeMatch[2], 10);
        inputDate.setHours(hours);
        inputDate.setMinutes(minutes);

        // Si le timepicker existe, on le met à jour visuellement
        if (this.picker.timepicker) {
          if (typeof this.picker.timepicker.setHours === "function") {
            this.picker.timepicker.setHours(hours);
            this.picker.timepicker.setMinutes(minutes);
          } else {
            // fallback pour les versions plus anciennes
            this.picker.timepicker.hours = hours;
            this.picker.timepicker.minutes = minutes;
          }
        }
      }
    }

    // ✅ Met à jour la sélection du calendrier
    const currentSelected = this.picker.selectedDates?.[0];
    if (!currentSelected || currentSelected.getTime() !== inputDate.getTime()) {
      this.picker.selectDate(inputDate, true);
      this.picker.setViewDate(inputDate);
    }

    // ✅ Met à jour le champ caché envoyé à Rails
    this.hiddenInput.value = this.toRailsDateFormat(inputDate);
  }, 10);
});


  }

  // --- Lors de la sélection d’une date/heure ---
  handleSelect(date) {
    if (!date) {
      this.hiddenInput.value = "";
      return;
    }

    // ✅ Conserver la date lors d’un changement d’heure
    if (
      this.hasTimepickerValue &&
      this.timepickerValue &&
      this.picker.selectedDates.length > 0
    ) {
      const prev = this.picker.selectedDates[0];
      if (prev && prev instanceof Date && !this.picker.onlyTimepicker) {
        date.setFullYear(prev.getFullYear());
        date.setMonth(prev.getMonth());
        date.setDate(prev.getDate());
      }
    }

    this.hiddenInput.value = this.toRailsDateFormat(date);

    if (this.hasReflexValue) {
      this.element.dispatchEvent(new CustomEvent("change", { bubbles: true }));
    }
  }

  /**
   * Parse timezone-safe:
   * - "YYYY-MM-DD" ou "YYYY-MM-DD HH:MM:SS" ou "YYYY-MM-DDTHH:MM:SSZ/+02:00"
   *   => on RECONSTRUIT en local (pas de new Date(str))
   * - sinon => parsing selon finalFormat (dd/MM/yyyy ou MM/dd/yyyy) + heure optionnelle
   */
  parseAnyDateStringToLocalDate(value, format) {
    if (!value || typeof value !== "string") return null;
    const str = value.trim();
    if (!str) return null;

    // Rails / ISO qui commence par YYYY-MM-DD
    const isoLike = str.match(
      /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?/
    );
    if (isoLike) {
      const y = parseInt(isoLike[1], 10);
      const m = parseInt(isoLike[2], 10);
      const d = parseInt(isoLike[3], 10);
      const hh = isoLike[4] ? parseInt(isoLike[4], 10) : 0;
      const mi = isoLike[5] ? parseInt(isoLike[5], 10) : 0;
      const ss = isoLike[6] ? parseInt(isoLike[6], 10) : 0;

      const dt = new Date(y, m - 1, d, hh, mi, ss);
      return isNaN(dt.getTime()) ? null : dt;
    }

    // Sinon format UI (dd/MM/yyyy ou MM/dd/yyyy) + heure optionnelle
    return this.parseInputDate(str, format);
  }

  // --- Parse la valeur entrée selon format UI ---
  parseInputDate(value, format) {
    if (!value || typeof value !== "string") return null;
    const str = value.trim();

    let day,
      month,
      year,
      hours = 0,
      minutes = 0;

    const dateTimeParts = str.split(" ");
    const datePart = dateTimeParts[0];
    const timePart = dateTimeParts[1] || "";

    const dateParts = datePart.split(/[\/\.\-]/);

    if (format.startsWith("dd")) {
      [day, month, year] = dateParts;
    } else if (format.startsWith("MM")) {
      [month, day, year] = dateParts;
    } else if (format.startsWith("yyyy")) {
      [year, month, day] = dateParts;
    }

    if (timePart) {
      const timeMatch = timePart.match(/(\d{1,2}):(\d{1,2})/);
      if (timeMatch) {
        hours = parseInt(timeMatch[1], 10);
        minutes = parseInt(timeMatch[2], 10);
      }
    }

    if (!day || !month || !year) return null;

    const y = parseInt(year, 10);
    const m = parseInt(month, 10);
    const d = parseInt(day, 10);

    const dt = new Date(y, m - 1, d, hours, minutes, 0);
    return isNaN(dt.getTime()) ? null : dt;
  }

  // --- Convertit vers format Rails (YYYY-MM-DD ou YYYY-MM-DD HH:MM:SS) ---
  toRailsDateFormat(date) {
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    const hh = String(date.getHours()).padStart(2, "0");
    const mi = String(date.getMinutes()).padStart(2, "0");
    const ss = "00";

    return this.hasTimepickerValue && this.timepickerValue
      ? `${yyyy}-${mm}-${dd} ${hh}:${mi}:${ss}`
      : `${yyyy}-${mm}-${dd}`;
  }

  handleFormSubmit() {
    const format = this.hasFormatValue
      ? this.formatValue
      : this.localeValue === "en"
      ? "MM/dd/yyyy"
      : "dd/MM/yyyy";

    const parsed = this.parseAnyDateStringToLocalDate(this.element.value, format);
    this.hiddenInput.value =
      parsed && !isNaN(parsed.getTime()) ? this.toRailsDateFormat(parsed) : "";
  }

disconnect() {
    this.picker?.destroy();
    Inputmask.remove(this.element); // Nettoie le masque Inputmask
    if (this.form) this.form.removeEventListener("submit", this.submitListener);
  }
}
